import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  VStack,
  HStack,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  Spinner,
  Center,
  Flex,
  Select,
  InputGroup,
  InputLeftElement,
  Icon,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Code,
  Divider
} from '@chakra-ui/react'
import { SearchIcon, WarningIcon, CheckIcon } from '@chakra-ui/icons'
import { useTranslation } from 'react-i18next'
import { getUserHistoryList } from '../../services/apiService'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'

type HistoryItem = {
  id: number
  type: string
  userId: number
  usdtAmount: string | null
  beforeUsdtBalance: string | null
  afterUsdtBalance: string | null
  pointAmount: string | null
  beforePointBalance: string | null
  afterPointBalance: string | null
  serviceDate: string | null
  beforeServiceDate: string | null
  afterServiceDate: string | null
  txid: string | null
  feeTxid: string | null
  calTxid: string | null
  txidTrc: string | null
  feeTxidTrc: string | null
  calTxidTrc: string | null
  byUserId: number | null
  byHistoryId: number | null
  toUserId: number | null
  toHistoryId: number | null
  withdrawalAmount: string | null
  fee: string | null
  network: string | null
  walletAddress: string | null
  payoutScheduledDate: string | null
  payoutStatus: string | null
  note1: string | null
  note2: string | null
  note3: string | null
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
  validation: {
    isValid: boolean
    errorType?: string
    expectedValue?: string
    actualValue?: string
    difference?: string
  }
  typeDescription?: string
}

type UserInfo = {
  id: number
  account: string
  username: string
}

type HistoryListResponse = {
  user: UserInfo
  histories: HistoryItem[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

const History = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [userId, setUserId] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<HistoryListResponse | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [selectedHistory, setSelectedHistory] = useState<HistoryItem | null>(null)

  const fetchList = async (override?: Partial<{ page: number; pageSize: number; userId: string }>) => {
    if (!override?.userId && !userId) {
      toast({
        title: '사용자 ID를 입력해주세요.',
        status: 'warning',
        duration: 2000
      })
      return
    }

    const targetUserId = override?.userId || userId
    if (!targetUserId || isNaN(Number(targetUserId))) {
      toast({
        title: '올바른 사용자 ID를 입력해주세요.',
        status: 'error',
        duration: 2000
      })
      return
    }

    try {
      setLoading(true)
      const response = await getUserHistoryList({
        userId: Number(targetUserId),
        page: override?.page || page,
        pageSize: override?.pageSize || pageSize
      })

      if (response.statusCode === 200) {
        setData(response.data)
      } else {
        toast({
          title: '조회 실패',
          description: response.message,
          status: 'error',
          duration: 3000
        })
      }
    } catch (error: any) {
      console.error('Error fetching history list:', error)
      toast({
        title: '조회 실패',
        description: error?.response?.data?.message || error?.message || '오류가 발생했습니다.',
        status: 'error',
        duration: 3000
      })
    } finally {
      setLoading(false)
    }
  }

  const onSearch = () => {
    setPage(1)
    fetchList({ page: 1, userId })
  }


  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    fetchList({ page: newPage, userId })
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(1)
    fetchList({ page: 1, pageSize: newPageSize, userId })
  }

  const formatUSDT = (value?: string | null) => {
    if (!value) return '-'
    return `${parseFloat(value).toFixed(8)} USDT`
  }

  const formatDate = (value?: string | null) => {
    if (!value) return '-'
    return new Date(value).toLocaleString('ko-KR')
  }

  const getTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      'DP': 'green',
      'WD_NO': 'red',
      'WD_RB': 'blue',
      'REWARD_PASS': 'cyan',
      'ADMIN_USDT': 'orange',
      'BUY_PASS': 'teal'
    }
    return colors[type] || 'gray'
  }

  const getValidationBadge = (validation: HistoryItem['validation']) => {
    if (validation.isValid) {
      return (
        <Badge colorScheme="green" size="sm">
          <CheckIcon mr={1} />
          정상
        </Badge>
      )
    } else {
      return (
        <Badge colorScheme="red" size="sm">
          <WarningIcon mr={1} />
          오류
        </Badge>
      )
    }
  }

  const openDetailModal = (history: HistoryItem) => {
    setSelectedHistory(history)
    onOpen()
  }

  const getExplorerUrl = (network?: string | null, txid?: string | null) => {
    if (!network || !txid) return null
    
    if (network === 'BSC') {
      return `https://bscscan.com/tx/${txid}`
    } else if (network === 'TRC20') {
      return `https://tronscan.org/#/transaction/${txid}`
    }
    return null
  }

  return (
    <DefaultLayout>
      <AdminNav />
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* 헤더 */}
        <Box>
          <Text fontSize="2xl" fontWeight="bold" mb={2} color="white">
            이력관리
          </Text>
          <Text color="gray.400">
            사용자별 USDT 잔액 이력을 조회하고 연속성을 검증합니다.
          </Text>
        </Box>

        {/* 검색 영역 */}
        <Box bg="dark.card" p={4} borderRadius="lg" border="1px solid" borderColor="whiteAlpha.200">
          <VStack spacing={4}>
            <HStack spacing={4}>
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <Icon as={SearchIcon} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="사용자 번호 입력"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && onSearch()}
                  bg="dark.bg"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _placeholder={{ color: 'gray.500' }}
                  _hover={{ borderColor: 'whiteAlpha.400' }}
                />
              </InputGroup>
              <Button
                colorScheme="cyan"
                bgGradient="linear(to-r, cyan.500, blue.500)"
                color="white"
                _hover={{ bgGradient: "linear(to-r, cyan.400, blue.400)", boxShadow: "0 0 10px rgba(0, 200, 255, 0.4)" }}
                onClick={onSearch}
                isLoading={loading}
                loadingText="조회중..."
              >
                조회
              </Button>
            </HStack>
            
            {/* 사용자 ID 네비게이션 버튼 */}
            {data && (
              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor="whiteAlpha.300"
                  color="gray.300"
                  _hover={{ bg: 'whiteAlpha.100', borderColor: 'whiteAlpha.400' }}
                  onClick={() => {
                    const prevUserId = (Number(userId) - 1).toString()
                    setUserId(prevUserId)
                    fetchList({ page: 1, userId: prevUserId })
                  }}
                  isDisabled={!userId || Number(userId) <= 1}
                >
                  이전 사용자
                </Button>
                <Text fontSize="sm" minW="120px" textAlign="center" color="white">
                  사용자 ID: {userId}
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor="whiteAlpha.300"
                  color="gray.300"
                  _hover={{ bg: 'whiteAlpha.100', borderColor: 'whiteAlpha.400' }}
                  onClick={() => {
                    const nextUserId = (Number(userId) + 1).toString()
                    setUserId(nextUserId)
                    fetchList({ page: 1, userId: nextUserId })
                  }}
                >
                  다음 사용자
                </Button>
              </HStack>
            )}
          </VStack>
        </Box>

        {/* 사용자 정보 및 집계 */}
        {data?.user && (
          <Box bg="dark.card" p={4} borderRadius="lg" border="1px solid" borderColor="whiteAlpha.200">
            <VStack spacing={4} align="stretch">
              {/* 사용자 정보 */}
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={2} color="white">
                  사용자 정보
                </Text>
                <HStack spacing={6}>
                  <Text color="gray.300"><strong>아이디:</strong> {data.user.id}</Text>
                  <Text color="gray.300"><strong>계정:</strong> {data.user.account}</Text>
                  <Text color="gray.300"><strong>이름:</strong> {data.user.username}</Text>
                </HStack>
              </Box>

              {/* 집계 정보 */}
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={2} color="white">
                  집계 정보
                </Text>
                <HStack spacing={8} wrap="wrap">
                  <Box>
                    <Text fontSize="sm" color="gray.400">총 USDT 수량</Text>
                    <Text fontSize="xl" fontWeight="bold" color="cyan.400">
                      {(() => {
                        const totalAmount = data.histories.reduce((sum, history) => {
                          const amount = parseFloat(history.usdtAmount || '0')
                          return sum + amount
                        }, 0)
                        return `${totalAmount.toFixed(8)} USDT`
                      })()}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.400">오류 금액</Text>
                    <Text fontSize="xl" fontWeight="bold" color="red.400">
                      {(() => {
                        const errorAmount = data.histories.reduce((sum, history) => {
                          if (!history.validation.isValid && history.validation.difference) {
                            return sum + parseFloat(history.validation.difference)
                          }
                          return sum
                        }, 0)
                        return `${errorAmount.toFixed(8)} USDT`
                      })()}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.400">총 이력 수</Text>
                    <Text fontSize="xl" fontWeight="bold" color="white">
                      {data.histories.length}개
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.400">오류 이력 수</Text>
                    <Text fontSize="xl" fontWeight="bold" color="red.400">
                      {data.histories.filter(h => !h.validation.isValid).length}개
                    </Text>
                  </Box>
                </HStack>
              </Box>
            </VStack>
          </Box>
        )}

        {/* 오류 요약 */}
        {data?.histories && (
          <Box>
            {data.histories.some(h => !h.validation.isValid) ? (
              <Alert status="error">
                <AlertIcon />
                <Box>
                  <AlertTitle>데이터 무결성 오류 발견!</AlertTitle>
                  <AlertDescription>
                    {data.histories.filter(h => !h.validation.isValid).length}개의 이력에서 연속성 오류가 발견되었습니다.
                  </AlertDescription>
                </Box>
              </Alert>
            ) : (
              <Alert status="success">
                <AlertIcon />
                <AlertTitle>모든 데이터가 정상입니다!</AlertTitle>
              </Alert>
            )}
          </Box>
        )}

        {/* 테이블 */}
        {data?.histories && (
          <Box bg="dark.card" borderRadius="lg" border="1px solid" borderColor="whiteAlpha.200" overflow="hidden">
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg="dark.card">
                  <Tr>
                    <Th>ID</Th>
                    <Th>타입</Th>
                    <Th>USDT 수량</Th>
                    <Th>이전 잔액</Th>
                    <Th>이후 잔액</Th>
                    <Th>상태</Th>
                    <Th>생성일</Th>
                    <Th>상세</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.histories.map((history) => (
                    <Tr
                      key={history.id}
                      bg={!history.validation.isValid ? 'red.50' : 'white'}
                      _hover={{ bg: !history.validation.isValid ? 'whiteAlpha.100' : 'gray.50' }}
                    >
                      <Td>{history.id}</Td>
                      <Td>
                        <VStack spacing={1} align="start">
                          <Badge colorScheme={getTypeColor(history.type)}>
                            {history.type}
                          </Badge>
                          {history.typeDescription && (
                            <Text fontSize="xs" color="gray.400">
                              {history.typeDescription}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>{formatUSDT(history.usdtAmount)}</Td>
                      <Td>{formatUSDT(history.beforeUsdtBalance)}</Td>
                      <Td>{formatUSDT(history.afterUsdtBalance)}</Td>
                      <Td>{getValidationBadge(history.validation)}</Td>
                      <Td>{formatDate(history.createdAt)}</Td>
                      <Td>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDetailModal(history)}
                        >
                          상세
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>

            {/* 페이지네이션 */}
            <Box p={4} borderTop="1px" borderColor="gray.200">
              <Flex justify="space-between" align="center">
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.400">
                    페이지 크기:
                  </Text>
                  <Select
                    size="sm"
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    maxW="100px"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Select>
                </HStack>
                
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(page - 1)}
                    isDisabled={page === 1}
                  >
                    이전
                  </Button>
                  <Text fontSize="sm">
                    {page} / {data.pagination.totalPages} 페이지
                    ({data.pagination.total}개 항목)
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(page + 1)}
                    isDisabled={page === data.pagination.totalPages}
                  >
                    다음
                  </Button>
                </HStack>
              </Flex>
            </Box>
          </Box>
        )}

        {/* 로딩 */}
        {loading && (
          <Center py={8}>
            <Spinner size="lg" />
          </Center>
        )}
      </VStack>

      {/* 상세 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>이력 상세 정보</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedHistory && (
              <VStack spacing={4} align="stretch">
                {/* 기본 정보 */}
                <Box>
                  <Text fontWeight="bold" mb={2}>기본 정보</Text>
                  <VStack spacing={2} align="stretch">
                    <HStack spacing={4} wrap="wrap">
                      <Text><strong>ID:</strong> {selectedHistory.id}</Text>
                      <Text><strong>사용자 ID:</strong> {selectedHistory.userId}</Text>
                    </HStack>
                    <HStack spacing={2} align="center">
                      <Text><strong>타입:</strong></Text>
                      <Badge colorScheme={getTypeColor(selectedHistory.type)}>
                        {selectedHistory.type}
                      </Badge>
                      {selectedHistory.typeDescription && (
                        <Text fontSize="sm" color="gray.400">
                          ({selectedHistory.typeDescription})
                        </Text>
                      )}
                    </HStack>
                  </VStack>
                </Box>

                <Divider />

                {/* USDT 정보 */}
                <Box>
                  <Text fontWeight="bold" mb={2}>USDT 정보</Text>
                  <VStack spacing={2} align="stretch">
                    <HStack justify="space-between">
                      <Text>USDT 수량:</Text>
                      <Text fontFamily="mono">{formatUSDT(selectedHistory.usdtAmount)}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>이전 잔액:</Text>
                      <Text fontFamily="mono">{formatUSDT(selectedHistory.beforeUsdtBalance)}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>이후 잔액:</Text>
                      <Text fontFamily="mono">{formatUSDT(selectedHistory.afterUsdtBalance)}</Text>
                    </HStack>
                  </VStack>
                </Box>

                <Divider />

                {/* 검증 결과 */}
                <Box>
                  <Text fontWeight="bold" mb={2}>검증 결과</Text>
                  {selectedHistory.validation.isValid ? (
                    <Alert status="success">
                      <CheckIcon />
                      <AlertDescription>데이터가 정상입니다.</AlertDescription>
                    </Alert>
                  ) : (
                    <Alert status="error">
                      <WarningIcon />
                      <Box>
                        <AlertTitle>연속성 오류 발견</AlertTitle>
                        <AlertDescription>
                          <VStack align="stretch" spacing={2} mt={2}>
                            <Text><strong>예상값:</strong> {selectedHistory.validation.expectedValue}</Text>
                            <Text><strong>실제값:</strong> {selectedHistory.validation.actualValue}</Text>
                            <Text><strong>차이:</strong> {selectedHistory.validation.difference} USDT</Text>
                          </VStack>
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}
                </Box>

                <Divider />

                {/* 트랜잭션 정보 */}
                {(selectedHistory.txid || selectedHistory.txidTrc) && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>트랜잭션 정보</Text>
                    <VStack spacing={2} align="stretch">
                      {selectedHistory.txid && (
                        <HStack justify="space-between">
                          <Text>BSC TXID:</Text>
                          <Code fontSize="xs">
                            {selectedHistory.txid.substring(0, 20)}...
                          </Code>
                        </HStack>
                      )}
                      {selectedHistory.txidTrc && (
                        <HStack justify="space-between">
                          <Text>TRC TXID:</Text>
                          <Code fontSize="xs">
                            {selectedHistory.txidTrc.substring(0, 20)}...
                          </Code>
                        </HStack>
                      )}
                      {selectedHistory.network && selectedHistory.txid && (
                        <Button
                          size="sm"
                          variant="outline"
                          as="a"
                          href={getExplorerUrl(selectedHistory.network, selectedHistory.txid) || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          탐색기에서 보기
                        </Button>
                      )}
                    </VStack>
                  </Box>
                )}

                {/* 기타 정보 */}
                <Box>
                  <Text fontWeight="bold" mb={2}>기타 정보</Text>
                  <VStack spacing={2} align="stretch">
                    <HStack justify="space-between">
                      <Text>생성일:</Text>
                      <Text>{formatDate(selectedHistory.createdAt)}</Text>
                    </HStack>
                    {selectedHistory.updatedAt && (
                      <HStack justify="space-between">
                        <Text>수정일:</Text>
                        <Text>{formatDate(selectedHistory.updatedAt)}</Text>
                      </HStack>
                    )}
                    {selectedHistory.note1 && (
                      <HStack justify="space-between">
                        <Text>메모1:</Text>
                        <Text>{selectedHistory.note1}</Text>
                      </HStack>
                    )}
                    {selectedHistory.note2 && (
                      <HStack justify="space-between">
                        <Text>메모2:</Text>
                        <Text>{selectedHistory.note2}</Text>
                      </HStack>
                    )}
                    {selectedHistory.note3 && (
                      <HStack justify="space-between">
                        <Text>메모3:</Text>
                        <Text>{selectedHistory.note3}</Text>
                      </HStack>
                    )}
                  </VStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
    </DefaultLayout>
  )
}

export default History
